// Das Blockmodell der Kuh: Quader aus BoxGeometry in einer Group, Ursprung ist die Fußpunkt-Mitte,
// der Blick geht bei yaw 0 entlang −z (dieselbe Konvention wie Kamera und Simulation). Der Bauplan
// (`kuhBauplan`) ist reine Daten aus den Maßen der Art und in Vitest prüfbar; erst `createKuhKit`
// macht daraus Three.js-Objekte. Die Fleckentextur entsteht aus einem deterministischen Hash
// (kein Math.random) und wird mit NearestFilter und Wiederholung in Blockeinheiten aufgetragen,
// damit die Pixel so groß sind wie die der Welt (16 je Block).

import {
  BoxGeometry,
  CanvasTexture,
  Group,
  Mesh,
  MeshLambertMaterial,
  NearestFilter,
  RepeatWrapping,
  SRGBColorSpace,
} from 'three';
import type { Species } from '@bauernhof/shared';
import type { AnimalModel, ModelKit } from '../animals.js';

/** Kantenlänge der Fleckentextur in Pixeln; ein Block der Welt hat 16 Pixel, die Kuh auch. */
export const SPOT_TEXTURE_SIZE = 16;
/** Salz der Fleckentextur; ein anderer Wert ergibt ein anderes, ebenso deterministisches Muster. */
export const SPOT_SEED = 7;
/** Pixel je Block, wie im Textur-Atlas der Welt. */
const TEXELS_PER_BLOCK = 16;

/** Die Materialien des Modells; jeder Quader des Bauplans nennt eines davon. */
export type KuhMaterial =
  | 'fleck'
  | 'weiss'
  | 'huf'
  | 'schnauze'
  | 'horn'
  | 'euter'
  | 'dunkel'
  | 'augapfel'
  | 'schwanz';

const FARBEN: Readonly<Record<Exclude<KuhMaterial, 'fleck'>, number>> = {
  weiss: 0xf0eee8,
  huf: 0x2e2a28,
  schnauze: 0xe8cbb8,
  horn: 0xe6dcc0,
  euter: 0xf0a0b0,
  dunkel: 0x1e1e20,
  // Augapfel reinweiß, damit er sich von den hellen Fellstellen abhebt; Schwanz braun, damit er
  // sowohl vor dunklen als auch vor hellen Flecken sichtbar bleibt.
  augapfel: 0xffffff,
  schwanz: 0x6b4a32,
};

/** Ein Quader des Bauplans: Kantenlängen und Mittelpunkt in Blöcken, relativ zu seinem Elternteil. */
export interface Quader {
  teil: string;
  material: KuhMaterial;
  size: [number, number, number];
  center: [number, number, number];
}

/** Ein Bein: Drehpunkt oben (relativ zum Fußpunkt), Teile hängen darunter; `sign` ist die Pendelrichtung. */
export interface Bein {
  name: string;
  sign: 1 | -1;
  pivot: [number, number, number];
  teile: Quader[];
}

export interface KuhBauplan {
  /** Feste Teile am Rumpf: Körper, Kopf, Schnauze, Hörner, Ohren, Augen, Schwanz. */
  rumpf: Quader[];
  beine: Bein[];
  /** Das Euter hängt an einem Drehpunkt am Bauch; die Skalierung wirkt dort, es zieht sich nach oben zusammen. */
  euter: { pivot: [number, number, number]; teile: Quader[] };
}

/** Die vier Beine mit Lage (x: links −1, rechts +1; z: vorn −1, hinten +1) und Pendelrichtung, diagonal gegenläufig. */
export const KUH_BEINE: readonly { name: string; x: -1 | 1; z: -1 | 1; sign: 1 | -1 }[] = [
  { name: 'vorne links', x: -1, z: -1, sign: 1 },
  { name: 'vorne rechts', x: 1, z: -1, sign: -1 },
  { name: 'hinten links', x: -1, z: 1, sign: -1 },
  { name: 'hinten rechts', x: 1, z: 1, sign: 1 },
];

/**
 * Der Bauplan aus den Maßen der Art (Breite quer, Höhe, Länge in Blickrichtung). Alle Teile
 * liegen in `[−w/2, w/2] × [0, h] × [−l/2, l/2]`: Hörner reichen bis `h`, Hufe bis 0, Schnauze
 * bis `−l/2`, Schwanz bis `+l/2`. Eine kleinere Art mit demselben Modell (Kalb) skaliert mit.
 */
export function kuhBauplan(species: Pick<Species, 'width' | 'height' | 'length'>): KuhBauplan {
  const w = species.width;
  const h = species.height;
  const l = species.length;

  const beinBreite = 0.2 * w;
  const beinHoehe = 0.42 * h;
  const hufHoehe = 0.08 * h;
  const koerperUnten = 0.36 * h;
  const koerperOben = 0.86 * h;
  const koerperVorn = -0.25 * l;
  const koerperHinten = 0.45 * l;
  const kopfUnten = 0.63 * h;
  const kopfOben = 0.95 * h;
  const kopfBreite = 0.5 * w;

  const rumpf: Quader[] = [
    {
      teil: 'koerper',
      material: 'fleck',
      size: [w, koerperOben - koerperUnten, koerperHinten - koerperVorn],
      center: [0, (koerperUnten + koerperOben) / 2, (koerperVorn + koerperHinten) / 2],
    },
    {
      teil: 'kopf',
      material: 'fleck',
      size: [kopfBreite, kopfOben - kopfUnten, 0.3 * l],
      center: [0, (kopfUnten + kopfOben) / 2, -0.31 * l],
    },
    {
      teil: 'schnauze',
      material: 'schnauze',
      size: [0.36 * w, 0.14 * h, 0.06 * l],
      center: [0, kopfUnten + 0.07 * h, -0.47 * l],
    },
    { teil: 'horn links', material: 'horn', size: [0.09 * w, 0.1 * h, 0.09 * w], center: [-0.16 * w, h - 0.05 * h, -0.25 * l] },
    { teil: 'horn rechts', material: 'horn', size: [0.09 * w, 0.1 * h, 0.09 * w], center: [0.16 * w, h - 0.05 * h, -0.25 * l] },
    { teil: 'ohr links', material: 'dunkel', size: [0.14 * w, 0.05 * h, 0.1 * l], center: [-0.32 * w, 0.88 * h, -0.28 * l] },
    { teil: 'ohr rechts', material: 'dunkel', size: [0.14 * w, 0.05 * h, 0.1 * l], center: [0.32 * w, 0.88 * h, -0.28 * l] },
    // Augen: weißer Augapfel, der seitlich aus dem Kopf herausragt, mit dunkler Pupille davor,
    // damit sie aus jeder Richtung als Augen zu erkennen sind (Kopf reicht bis ±kopfBreite/2).
    ...([-1, 1] as const).flatMap((seite): Quader[] => {
      const name = seite === -1 ? 'links' : 'rechts';
      const augeX = seite * (kopfBreite / 2 + 0.02 * w);
      const pupilleX = seite * (kopfBreite / 2 + 0.07 * w);
      return [
        { teil: `augapfel ${name}`, material: 'augapfel', size: [0.08 * w, 0.09 * h, 0.09 * l], center: [augeX, 0.84 * h, -0.4 * l] },
        { teil: `auge ${name}`, material: 'dunkel', size: [0.04 * w, 0.05 * h, 0.05 * l], center: [pupilleX, 0.84 * h, -0.41 * l] },
      ];
    }),
    // Schwanz: braun, deutlich hinter dem Rumpf, mit heller Quaste unten.
    {
      teil: 'schwanz',
      material: 'schwanz',
      size: [0.1 * w, 0.4 * h, l / 2 - koerperHinten],
      center: [0, koerperOben - 0.2 * h, (koerperHinten + l / 2) / 2],
    },
    {
      teil: 'schwanzquaste',
      material: 'weiss',
      size: [0.16 * w, 0.12 * h, l / 2 - koerperHinten],
      center: [0, koerperOben - 0.4 * h - 0.06 * h, (koerperHinten + l / 2) / 2],
    },
  ];

  const beinX = w / 2 - beinBreite / 2 - 0.02;
  const beinVorn = koerperVorn + beinBreite / 2 + 0.03 * l;
  const beinHinten = koerperHinten - beinBreite / 2 - 0.03 * l;
  const beine: Bein[] = KUH_BEINE.map(({ name, x, z, sign }) => ({
    name,
    sign,
    pivot: [x * beinX, beinHoehe, z === -1 ? beinVorn : beinHinten],
    teile: [
      { teil: 'bein', material: 'weiss', size: [beinBreite, beinHoehe, beinBreite], center: [0, -beinHoehe / 2, 0] },
      {
        teil: 'huf',
        material: 'huf',
        size: [beinBreite + 0.02, hufHoehe, beinBreite + 0.02],
        center: [0, -beinHoehe + hufHoehe / 2, 0],
      },
    ],
  }));

  const euter: KuhBauplan['euter'] = {
    pivot: [0, koerperUnten + 0.01 * h, 0.26 * l],
    teile: [{ teil: 'euter', material: 'euter', size: [0.44 * w, 0.18 * h, 0.24 * l], center: [0, -0.09 * h, 0] }],
  };

  return { rumpf, beine, euter };
}

/** Ganzzahl-Hash aus drei Werten, gleichmäßig auf [0, 1) verteilt; dieselbe Bauart wie im Atlas. */
function hash(x: number, y: number, salz: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(salz, 2246822519)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const FLECK_DUNKEL = [30, 30, 32] as const;
const FLECK_HELL = [240, 238, 232] as const;
/** Anzahl der Flecken je Kachel. */
const FLECKEN = 4;

/**
 * Malt die Fleckenkachel als RGBA-Pixel, zeilenweise von oben: helle Grundfarbe mit dunklen,
 * unregelmäßig berandeten Flecken. Die Flecken liegen auf einem Torus, damit die Kachel sich
 * nahtlos wiederholt. Deterministisch aus `seed`; kein Math.random.
 */
export function paintSpots(size: number, seed: number): Uint8ClampedArray<ArrayBuffer> {
  const flecken: { x: number; y: number; r: number }[] = [];
  for (let i = 0; i < FLECKEN; i++) {
    flecken.push({
      x: hash(i, 0, seed) * size,
      y: hash(i, 1, seed) * size,
      r: (0.14 + hash(i, 2, seed) * 0.12) * size,
    });
  }
  const data = new Uint8ClampedArray(new ArrayBuffer(size * size * 4));
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let dunkel = false;
      const rand = (hash(x, y, seed + 99) - 0.5) * 0.08 * size;
      for (const fleck of flecken) {
        const dx = Math.min(Math.abs(x + 0.5 - fleck.x), size - Math.abs(x + 0.5 - fleck.x));
        const dy = Math.min(Math.abs(y + 0.5 - fleck.y), size - Math.abs(y + 0.5 - fleck.y));
        if (Math.hypot(dx, dy) < fleck.r + rand) {
          dunkel = true;
          break;
        }
      }
      const farbe = dunkel ? FLECK_DUNKEL : FLECK_HELL;
      const i = (y * size + x) * 4;
      data[i] = farbe[0];
      data[i + 1] = farbe[1];
      data[i + 2] = farbe[2];
      data[i + 3] = 255;
    }
  }
  return data;
}

/**
 * Skaliert die UVs einer BoxGeometry so, dass jede Seite so viele Kacheln zeigt, wie sie Blöcke
 * lang ist; mit `RepeatWrapping` hat die Textur dann überall dieselbe Pixeldichte. Verlässt sich
 * auf die Reihenfolge der BoxGeometry (+x, −x, +y, −y, +z, −z, je vier Ecken bei einem Segment).
 */
export function boxUvsInBlocks(geometry: BoxGeometry, width: number, height: number, depth: number): void {
  const uv = geometry.getAttribute('uv');
  const seiten: [number, number][] = [
    [depth, height],
    [depth, height],
    [width, depth],
    [width, depth],
    [width, height],
    [width, height],
  ];
  seiten.forEach(([u, v], seite) => {
    for (let i = seite * 4; i < seite * 4 + 4; i++) {
      uv.setXY(i, uv.getX(i) * u, uv.getY(i) * v);
    }
  });
  uv.needsUpdate = true;
}

/** Lädt die Fleckenkachel in ein Canvas und macht eine ungefilterte, wiederholende Textur daraus. */
function createSpotTexture(): CanvasTexture {
  const pixel = paintSpots(SPOT_TEXTURE_SIZE, SPOT_SEED);
  const canvas = document.createElement('canvas');
  canvas.width = SPOT_TEXTURE_SIZE;
  canvas.height = SPOT_TEXTURE_SIZE;
  const kontext = canvas.getContext('2d');
  if (kontext === null) throw new Error('Kein 2D-Kontext für die Fleckentextur der Kuh.');
  kontext.putImageData(new ImageData(pixel, SPOT_TEXTURE_SIZE, SPOT_TEXTURE_SIZE), 0, 0);

  const texture = new CanvasTexture(canvas);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Der Baukasten für das Modell `kuh`: hält Textur und Materialien einmal für alle Kühe und baut je
 * Tier eine Group mit eigenen Geometrien (die Maße kommen aus der Art). Braucht das DOM für das
 * Textur-Canvas; der Bauplan darüber ist ohne DOM prüfbar.
 */
export function createKuhKit(): ModelKit {
  const textur = createSpotTexture();
  const materialien: Readonly<Record<KuhMaterial, MeshLambertMaterial>> = {
    fleck: new MeshLambertMaterial({ map: textur }),
    weiss: new MeshLambertMaterial({ color: FARBEN.weiss }),
    huf: new MeshLambertMaterial({ color: FARBEN.huf }),
    schnauze: new MeshLambertMaterial({ color: FARBEN.schnauze }),
    horn: new MeshLambertMaterial({ color: FARBEN.horn }),
    euter: new MeshLambertMaterial({ color: FARBEN.euter }),
    dunkel: new MeshLambertMaterial({ color: FARBEN.dunkel }),
    augapfel: new MeshLambertMaterial({ color: FARBEN.augapfel }),
    schwanz: new MeshLambertMaterial({ color: FARBEN.schwanz }),
  };

  const baueQuader = (quader: Quader, geometrien: BoxGeometry[]): Mesh => {
    const [sx, sy, sz] = quader.size;
    const geometry = new BoxGeometry(sx, sy, sz);
    if (quader.material === 'fleck') {
      // Eine Kachel deckt SPOT_TEXTURE_SIZE Pixel; bei 16 Pixeln je Block ist das genau ein Block.
      const kachelnJeBlock = TEXELS_PER_BLOCK / SPOT_TEXTURE_SIZE;
      boxUvsInBlocks(geometry, sx * kachelnJeBlock, sy * kachelnJeBlock, sz * kachelnJeBlock);
    }
    geometrien.push(geometry);
    const mesh = new Mesh(geometry, materialien[quader.material]);
    mesh.position.set(...quader.center);
    mesh.name = quader.teil;
    return mesh;
  };

  return {
    create(species: Species): AnimalModel {
      const plan = kuhBauplan(species);
      const geometrien: BoxGeometry[] = [];
      const root = new Group();
      root.name = `kuh:${species.id}`;

      for (const quader of plan.rumpf) root.add(baueQuader(quader, geometrien));

      const beine = plan.beine.map((bein) => {
        const pivot = new Group();
        pivot.name = `bein ${bein.name}`;
        pivot.position.set(...bein.pivot);
        for (const quader of bein.teile) pivot.add(baueQuader(quader, geometrien));
        root.add(pivot);
        return { pivot, sign: bein.sign };
      });

      const euter = new Group();
      euter.name = 'euter';
      euter.position.set(...plan.euter.pivot);
      for (const quader of plan.euter.teile) euter.add(baueQuader(quader, geometrien));
      root.add(euter);

      return {
        object: root,
        setLegSwing(swing) {
          for (const bein of beine) bein.pivot.rotation.x = bein.sign * swing;
        },
        setUdderScale(scale) {
          euter.scale.setScalar(scale);
        },
        dispose() {
          for (const geometry of geometrien) geometry.dispose();
          geometrien.length = 0;
        },
      };
    },
    dispose() {
      for (const material of Object.values(materialien)) material.dispose();
      textur.dispose();
    },
  };
}
